import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk, RootState } from './types';
import * as userService from '../service/userService';
import { GetUserResponse } from '../types/GetUserResponse';
import { displayErrorNotification, displaySuccessNotification } from './notificationSlice';
import { getOrRefreshAccessToken } from './authenticationSlice';
import DeviceLogoutReason from '../types/DeviceLogoutReason';
import AddCardBlockedEvent from '../types/AddCardBlockedEvent';

export enum UserStatus {
  Idle = 'Idle',
  Loading = 'Loading',
  Success = 'Success',
  Error = 'Error',
}

type AddCardBlockedEventsKey = 'initiator' | 'impactedUser';

export interface AddCardBlockedEventsState {
  status: UserStatus;
  events: AddCardBlockedEvent[];
  totalPages: number;
  error?: string;
}

export interface UserSlice {
  status: UserStatus;
  getUserResponse: GetUserResponse | undefined;
  error: string | undefined;
  isDeleting: boolean;
  isRevoking: boolean;
  isSyncingWallet: boolean;
  loggingOutDevices: Record<string, boolean>;
  deletingCards: Record<string, boolean>;
  addCardBlockedEvents: Record<AddCardBlockedEventsKey, AddCardBlockedEventsState>;
  deletingAddCardBlockedEvents: Record<string, boolean>;
}

export const initialState: UserSlice = {
  status: UserStatus.Idle,
  getUserResponse: undefined,
  error: undefined,
  isDeleting: false,
  isRevoking: false,
  isSyncingWallet: false,
  loggingOutDevices: {},
  deletingCards: {},
  addCardBlockedEvents: {
    impactedUser: {
      status: UserStatus.Idle,
      events: [],
      totalPages: 0,
      error: undefined,
    },
    initiator: {
      status: UserStatus.Idle,
      events: [],
      totalPages: 0,
      error: undefined,
    },
  },
  deletingAddCardBlockedEvents: {},
};

/* Reducer */
export const userSlice = createSlice({
  name: 'getUser',
  initialState,
  reducers: {
    // Get
    getUserStart: (state: UserSlice) => {
      state.status = UserStatus.Loading;
      state.getUserResponse = undefined;
    },
    getUserSuccess: (state: UserSlice, action: PayloadAction<GetUserResponse>) => {
      state.status = UserStatus.Success;
      state.getUserResponse = action.payload;
    },
    getUserError: (state: UserSlice, action: PayloadAction<string>) => {
      state.status = UserStatus.Error;
      state.error = action.payload;
    },
    // Delete
    deleteUserStart: (state: UserSlice) => {
      state.isDeleting = true;
    },
    deleteUserEnd: (state: UserSlice) => {
      state.isDeleting = false;
    },
    // Revoke
    revokeUserStart: (state: UserSlice) => {
      state.isRevoking = true;
    },
    revokeUserEnd: (state: UserSlice) => {
      state.isRevoking = false;
    },
    // Device logout
    logoutDeviceStart: (state: UserSlice, action: PayloadAction<string>) => {
      state.loggingOutDevices[action.payload] = true;
    },
    logoutDeviceEnd: (state: UserSlice, action: PayloadAction<string>) => {
      delete state.loggingOutDevices[action.payload];
    },
    // Sync wallet
    syncWalletStart: (state: UserSlice) => {
      state.isSyncingWallet = true;
    },
    syncWalletEnd: (state: UserSlice) => {
      state.isSyncingWallet = false;
    },
    // Delete card
    deleteCardStart: (state: UserSlice, action: PayloadAction<string>) => {
      state.deletingCards[action.payload] = true;
    },
    deleteCardEnd: (state: UserSlice, action: PayloadAction<string>) => {
      delete state.deletingCards[action.payload];
    },
    // Add card blocked events (paginated)
    getAddCardBlockedEventsStart: (state: UserSlice, action: PayloadAction<AddCardBlockedEventsKey>) => {
      const key = action.payload;
      state.addCardBlockedEvents[key].status = UserStatus.Loading;
      state.addCardBlockedEvents[key].error = undefined;
    },
    getAddCardBlockedEventsSuccess: (
      state: UserSlice,
      action: PayloadAction<{ key: AddCardBlockedEventsKey; events: AddCardBlockedEvent[]; totalPages: number }>,
    ) => {
      const { key, events, totalPages } = action.payload;
      state.addCardBlockedEvents[key].status = UserStatus.Success;
      state.addCardBlockedEvents[key].events = events;
      state.addCardBlockedEvents[key].totalPages = totalPages;
    },
    getAddCardBlockedEventsError: (
      state: UserSlice,
      action: PayloadAction<{ key: AddCardBlockedEventsKey; error: string }>,
    ) => {
      const { key, error } = action.payload;
      state.addCardBlockedEvents[key].status = UserStatus.Error;
      state.addCardBlockedEvents[key].error = error;
    },
    // Delete add-card-blocked event
    deleteAddCardBlockedEventStart: (state: UserSlice, action: PayloadAction<string>) => {
      state.deletingAddCardBlockedEvents[action.payload] = true;
    },
    deleteAddCardBlockedEventEnd: (state: UserSlice, action: PayloadAction<string>) => {
      delete state.deletingAddCardBlockedEvents[action.payload];
    },
    removeAddCardBlockedEventFromState: (state: UserSlice, action: PayloadAction<{ eventId: string }>) => {
      const { eventId } = action.payload;

      (Object.keys(state.addCardBlockedEvents) as AddCardBlockedEventsKey[]).forEach((key) => {
        state.addCardBlockedEvents[key].events = state.addCardBlockedEvents[key].events.filter((e) => e.id !== eventId);
      });
    },
  },
});

export const {
  getUserStart,
  getUserSuccess,
  getUserError,
  deleteUserStart,
  deleteUserEnd,
  revokeUserStart,
  revokeUserEnd,
  logoutDeviceStart,
  logoutDeviceEnd,
  syncWalletStart,
  syncWalletEnd,
  deleteCardStart,
  deleteCardEnd,
  getAddCardBlockedEventsStart,
  getAddCardBlockedEventsSuccess,
  getAddCardBlockedEventsError,
  deleteAddCardBlockedEventStart,
  deleteAddCardBlockedEventEnd,
  removeAddCardBlockedEventFromState,
} = userSlice.actions;

/* Selectors */
export const selectUserStatus = (state: RootState) => state.user.status;
export const selectUserId = (state: RootState) => state.user.getUserResponse?.user.bpcAccountId;
export const selectUserResponse = (state: RootState) => state.user.getUserResponse;
export const selectUserError = (state: RootState) => state.user.error;
export const selectIsDeletingUser = (state: RootState) => state.user.isDeleting;
export const selectIsRevokingUser = (state: RootState) => state.user.isRevoking;
export const selectIsDeviceLoggingOut = (deviceId: string) => (state: RootState) =>
  state.user.loggingOutDevices[deviceId];
export const isSyncingWallet = (state: RootState) => state.user.isSyncingWallet;
export const isDeletingCard = (cardId: string) => (state: RootState) => state.user.deletingCards[cardId];

const toKey = (isInitiator: boolean): AddCardBlockedEventsKey => (isInitiator ? 'initiator' : 'impactedUser');

export const selectAddCardBlockedEvents = (isInitiator: boolean) => (state: RootState) =>
  state.user.addCardBlockedEvents[toKey(isInitiator)].events;
export const selectAddCardBlockedEventsTotalPages = (isInitiator: boolean) => (state: RootState) =>
  state.user.addCardBlockedEvents[toKey(isInitiator)].totalPages;
export const selectAddCardBlockedEventsStatus = (isInitiator: boolean) => (state: RootState) =>
  state.user.addCardBlockedEvents[toKey(isInitiator)].status;
export const selectAddCardBlockedEventsError = (isInitiator: boolean) => (state: RootState) =>
  state.user.addCardBlockedEvents[toKey(isInitiator)].error;

export const isDeletingAddCardBlockedEvent = (eventId: string) => (state: RootState) =>
  state.user.deletingAddCardBlockedEvents[eventId];

/* Thunk */
export const getUser =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    if (selectUserStatus(getState()) === UserStatus.Loading) {
      return;
    }

    try {
      dispatch(getUserStart());
      const accessToken = await getOrRefreshAccessToken(dispatch, getState);
      const getUserResponse = await userService.getUser(id, accessToken);
      dispatch(getUserSuccess(getUserResponse));
    } catch (e) {
      dispatch(getUserError(e.message));
    }
  };

export const deleteUser =
  ({
    userId,
    successMessage,
    errorMessage,
  }: {
    userId: string;
    successMessage: () => string;
    errorMessage: (e: Error) => string;
  }): AppThunk =>
  async (dispatch, getState) => {
    if (selectIsRevokingUser(getState())) {
      return;
    }

    try {
      dispatch(deleteUserStart());
      const accessToken = await getOrRefreshAccessToken(dispatch, getState);
      await userService.deleteUser(userId, accessToken);
      dispatch(displaySuccessNotification(successMessage()));
    } catch (e) {
      dispatch(displayErrorNotification(errorMessage(e)));
    } finally {
      dispatch(deleteUserEnd());
    }
  };

export const revokeUser =
  ({
    userId,
    successMessage,
    errorMessage,
  }: {
    userId: string;
    successMessage: () => string;
    errorMessage: (e: Error) => string;
  }): AppThunk =>
  async (dispatch, getState) => {
    if (selectIsDeletingUser(getState())) {
      return;
    }

    try {
      dispatch(revokeUserStart());
      const accessToken = await getOrRefreshAccessToken(dispatch, getState);
      await userService.revokeUser(userId, accessToken);
      dispatch(displaySuccessNotification(successMessage()));
      dispatch(getUser(userId));
    } catch (e) {
      dispatch(displayErrorNotification(errorMessage(e)));
    } finally {
      dispatch(revokeUserEnd());
    }
  };

export const logoutDevice =
  ({
    userId,
    deviceId,
    reason,
    successMessage,
    errorMessage,
  }: {
    userId: string;
    deviceId: string;
    reason: DeviceLogoutReason;
    successMessage: () => string;
    errorMessage: (e: Error) => string;
  }): AppThunk =>
  async (dispatch, getState) => {
    if (selectIsDeviceLoggingOut(deviceId)(getState())) {
      return;
    }

    try {
      dispatch(logoutDeviceStart(deviceId));
      const accessToken = await getOrRefreshAccessToken(dispatch, getState);
      await userService.logoutDevice(
        {
          userId,
          deviceId,
        },
        reason,
        accessToken,
      );
      dispatch(displaySuccessNotification(successMessage()));

      // refresh the user data
      const userid = selectUserResponse(getState())?.user.bpcAccountId;
      if (userid) {
        dispatch(getUser(userid));
      }
    } catch (e) {
      dispatch(displayErrorNotification(errorMessage(e)));
    } finally {
      dispatch(logoutDeviceEnd(deviceId));
    }
  };

export const syncWallet =
  ({
    userId,
    successMessage,
    errorMessage,
  }: {
    userId: string;
    successMessage: () => string;
    errorMessage: (e: Error) => string;
  }): AppThunk =>
  async (dispatch, getState) => {
    if (isSyncingWallet(getState())) {
      return;
    }

    try {
      dispatch(syncWalletStart());
      const accessToken = await getOrRefreshAccessToken(dispatch, getState);
      await userService.syncWallet(
        {
          userId,
        },
        accessToken,
      );
      dispatch(displaySuccessNotification(successMessage()));

      // refresh the user data
      const userid = selectUserResponse(getState())?.user.bpcAccountId;
      if (userid) {
        dispatch(getUser(userid));
      }
    } catch (e) {
      dispatch(displayErrorNotification(errorMessage(e)));
    } finally {
      dispatch(syncWalletEnd());
    }
  };

export const deleteCard =
  ({
    userId,
    cardId,
    successMessage,
    errorMessage,
  }: {
    userId: string;
    cardId: string;
    successMessage: () => string;
    errorMessage: (e: Error) => string;
  }): AppThunk =>
  async (dispatch, getState) => {
    if (isDeletingCard(cardId)(getState())) {
      return;
    }

    try {
      dispatch(deleteCardStart(cardId));
      const accessToken = await getOrRefreshAccessToken(dispatch, getState);
      await userService.deleteCard(
        {
          userId,
          cardId,
        },
        accessToken,
      );
      dispatch(displaySuccessNotification(successMessage()));

      // refresh the user data
      const userid = selectUserResponse(getState())?.user.bpcAccountId;
      if (userid) {
        dispatch(getUser(userid));
      }
    } catch (e) {
      dispatch(displayErrorNotification(errorMessage(e)));
    } finally {
      dispatch(deleteCardEnd(cardId));
    }
  };

export const getUserAddCardBlockedEvents =
  ({ page, pageSize, isInitiator }: { page: number; pageSize: number; isInitiator: boolean }): AppThunk =>
  async (dispatch, getState) => {
    const key: AddCardBlockedEventsKey = isInitiator ? 'initiator' : 'impactedUser';

    // Avoid duplicate call if already loading for this table
    if (selectAddCardBlockedEventsStatus(isInitiator)(getState()) === UserStatus.Loading) {
      return;
    }

    try {
      dispatch(getAddCardBlockedEventsStart(key));

      const accessToken = await getOrRefreshAccessToken(dispatch, getState);
      const userId = selectUserId(getState());

      if (!userId) {
        dispatch(getAddCardBlockedEventsError({ key, error: 'Missing userId' }));
        return;
      }

      const res = await userService.getAddCardBlockedEvents(userId, accessToken, page, pageSize, isInitiator);

      dispatch(
        getAddCardBlockedEventsSuccess({
          key,
          events: res.events ?? [],
          totalPages: res.totalPages ?? 0,
        }),
      );
    } catch (e) {
      dispatch(getAddCardBlockedEventsError({ key, error: e.message ?? 'Unknown Error' }));
    }
  };

export const deleteAddCardBlockedEvent =
  ({
    userId,
    eventId,
    successMessage,
    errorMessage,
  }: {
    userId: string;
    eventId: string;
    successMessage: () => string;
    errorMessage: (e: Error) => string;
  }): AppThunk =>
  async (dispatch, getState) => {
    if (isDeletingAddCardBlockedEvent(eventId)(getState())) {
      return;
    }

    try {
      dispatch(deleteAddCardBlockedEventStart(eventId));

      const accessToken = await getOrRefreshAccessToken(dispatch, getState);

      await userService.deleteAddCardBlockedEvent(userId, accessToken, eventId);

      dispatch(removeAddCardBlockedEventFromState({ eventId }));

      dispatch(displaySuccessNotification(successMessage()));
    } catch (e) {
      dispatch(displayErrorNotification(errorMessage(e as Error)));
    } finally {
      dispatch(deleteAddCardBlockedEventEnd(eventId));
    }
  };
